/**
 * RTSP URL templates by manufacturer, for devices connected by direct RTSP.
 * They only fill the path fields; the API stores the resulting paths.
 */
export type RtspPreset = 'hikvision' | 'dahua' | 'custom';

export const rtspPresetLabels: Record<RtspPreset, string> = {
  hikvision: 'Hikvision / HiLook',
  dahua: 'Intelbras / Dahua',
  custom: 'Personalizado',
};

export function presetPaths(
  preset: RtspPreset,
  channel: number,
): { main: string; sub: string } | null {
  const ch = Math.max(1, Math.trunc(channel) || 1);
  switch (preset) {
    case 'hikvision':
      return {
        main: `/Streaming/Channels/${String(ch)}01`,
        sub: `/Streaming/Channels/${String(ch)}02`,
      };
    case 'dahua':
      return {
        main: `/cam/realmonitor?channel=${String(ch)}&subtype=0`,
        sub: `/cam/realmonitor?channel=${String(ch)}&subtype=1`,
      };
    case 'custom':
      return null;
  }
}

/** Recognises stored paths so an edited device shows its template and channel. */
export function detectPreset(main: string, sub: string): { preset: RtspPreset; channel: number } {
  const hik = /^\/Streaming\/Channels\/(\d+)01$/i.exec(main);
  const dahua = /^\/cam\/realmonitor\?channel=(\d+)&subtype=0$/i.exec(main);
  for (const [preset, m] of [
    ['hikvision', hik],
    ['dahua', dahua],
  ] as const) {
    if (!m) continue;
    const channel = Number(m[1]);
    const expected = presetPaths(preset, channel);
    if (channel >= 1 && expected && (sub === '' || sub === expected.sub))
      return { preset, channel };
  }
  return { preset: main === '' ? 'hikvision' : 'custom', channel: 1 };
}

/** Placeholder the API replaces with each channel's number (ADR-018). */
export const CHANNEL = '{channel}';

/** A recorder's path templates for a preset, with {channel} in place of the number. */
export function presetTemplates(preset: RtspPreset): { main: string; sub: string } | null {
  switch (preset) {
    case 'hikvision':
      return { main: `/Streaming/Channels/${CHANNEL}01`, sub: `/Streaming/Channels/${CHANNEL}02` };
    case 'dahua':
      return {
        main: `/cam/realmonitor?channel=${CHANNEL}&subtype=0`,
        sub: `/cam/realmonitor?channel=${CHANNEL}&subtype=1`,
      };
    case 'custom':
      return null;
  }
}

/** Recognises a recorder's stored templates. */
export function detectTemplatePreset(main: string, sub: string): RtspPreset {
  if (main === '') return 'hikvision';
  for (const preset of ['hikvision', 'dahua'] as const) {
    const t = presetTemplates(preset);
    if (t && main === t.main && (sub === '' || sub === t.sub)) return preset;
  }
  return 'custom';
}

/** Fills a recorder template for one channel (preview of what the API stores). */
export const renderTemplate = (template: string, channel: number) =>
  template.replaceAll(CHANNEL, String(channel));
